import { useGlobalSearchParams, usePathname } from "expo-router";
import { useCallback, useMemo } from "react";
import { serializeQueryString, getCurrentUrl } from "@/helper/Request";
import isEmpty from "lodash/isEmpty";

export const useCurrentUrl = (options) => {
    const {
        url: _url = null,
        params: _params = null,
        splitParams = true,
    } = options ?? {};

    const {
        url: hookUrl,
        ...hookRest
    } = useGlobalSearchParams();

    const pathname = usePathname();

    const url = useMemo(() => _url ?? hookUrl ?? null, [hookUrl, _url]);
    const rest = useMemo(() => _params ?? hookRest ?? {}, [hookRest, _params]);

    const path = useMemo(() => getCurrentUrl({ url }, false), [url]);
    const params = useMemo(() => serializeQueryString(rest), [rest]);

    const fullPath = useMemo(() => {
        const path = getCurrentUrl({ url, params: rest }, true);
        let returnPath = path ?? pathname;
        
        if (returnPath?.endsWith("/")) returnPath = returnPath.slice(0, -1);
        if (returnPath?.endsWith("?")) returnPath = returnPath.slice(0, -1);

        return returnPath;
    }, [url, rest, pathname]);

    const rawUrlPath = useMemo(() => {
        return pathname.split("?")[0].split("#")[0];
    }, [pathname]);

    const serializeQuery = useCallback((paramsObj) => {
        return serializeQueryString(paramsObj);
    }, []);

    const buildQueryString = useCallback((newParams) => {
        return serializeQueryString({ ...rest, ...newParams });
    }, [rest]);

    const buildUrl = useCallback((newParams) => {
        return getCurrentUrl({ url, params: { ...rest, ...newParams } }, true);
    }, [url, rest]);

    const paramsObj = useMemo(() => {
        let obj = {};

        Object.keys(rest).forEach((key) => {
            const value = rest[key] ?? null;

            if (value == null || value == "null") return;
            if (value == undefined || value == "undefined") return;
            if (isEmpty(value)) return;

            if (typeof value == "string" && value?.includes(",")) {
                obj[key] = splitParams ? (value?.split(",")?.map((i) => i?.trim()) ?? []) : value;
                return;
            }

            if (Array.isArray(value) && value.length == 0) return;

            obj[key] = rest[key];
        });

        return obj;
    }, [rest, splitParams]);

    const paramKeys = useMemo(() => Object.keys(paramsObj || {}), [paramsObj]);

    const hasParams = useMemo(() => paramKeys.length > 0, [paramKeys]);

    return {
        url: path,
        rawUrlPath,
        fullPath,
        pathname,
        params,
        paramsObj,
        hasParams,
        paramKeys,
        buildQueryString,
        serializeQuery,
        buildUrl,
    };
};